import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { mobileStorage } from '@/utils/mobileStorage';

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const useSessionTracking = () => {
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    // Check if session ID already exists in sessionStorage so we don't recreate on refresh
    let sessionId = sessionStorage.getItem('pepite_session_id');
    if (!sessionId) {
      const newId = generateUUID();
      sessionStorage.setItem('pepite_session_id', newId);
      sessionId = newId;
    }

    let startTime = sessionStorage.getItem('pepite_session_start');
    if (!startTime) {
      const newStartTime = Date.now().toString();
      sessionStorage.setItem('pepite_session_start', newStartTime);
      startTime = newStartTime;
    }

    sessionIdRef.current = sessionId;
    startTimeRef.current = parseInt(startTime, 10);

    const apiBaseUrl = process.env.REACT_APP_API_URL || (Capacitor.isNativePlatform() ? 'https://pepitespot.fr/api' : '/api');

    const sendPing = async () => {
      if (!sessionIdRef.current || !startTimeRef.current) return;

      const durationSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);

      const payload = JSON.stringify({
        session_id: sessionIdRef.current,
        duration_seconds: durationSeconds
      });

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const isNative = Capacitor.isNativePlatform();
        if (isNative) {
          headers['X-Client-Platform'] = 'mobile';
          const authToken = mobileStorage.getAuthTokenSync() || (await mobileStorage.getAuthToken());
          if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
          }
        }

        const isAuthenticated = localStorage.getItem('is_authenticated') === 'true';
        if (!isAuthenticated) {
          const guestId = localStorage.getItem('guest_id') || mobileStorage.getItemSync('guest_id');
          if (guestId) {
            headers['X-Guest-ID'] = guestId;
          }
        }

        if (localStorage.getItem('visitor_opt_out') === 'true') {
          headers['X-Visitor-Opt-Out'] = '1';
        }

        await fetch(`${apiBaseUrl}/stats/session-duration`, {
          method: 'POST',
          body: payload,
          headers,
          credentials: 'include',
          keepalive: true
        });
      } catch (err) {
        // Silently fail if tracking is blocked
      }
    };

    // 1. Periodic ping every 10 seconds to ensure we capture time accurately even if they don't close tab properly
    const intervalId = setInterval(() => {
      void sendPing();
    }, 10000);

    // 2. Track when visibility changes (user switches tabs or minimizes app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void sendPing();
      }
    };

    // 3. Track when the window is actually about to unload
    const handleBeforeUnload = () => {
      void sendPing();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // 4. Native Mobile (Capacitor): send accumulated session duration when app goes to background
    let appStateListenerPromise: Promise<{ remove: () => Promise<void> }> | null = null;
    if (Capacitor.isNativePlatform()) {
      appStateListenerPromise = App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          void sendPing();
        }
      });
    }

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (appStateListenerPromise) {
        appStateListenerPromise.then(listener => listener.remove()).catch(() => {});
      }
    };
  }, []);
};
